import { alpha, styled } from '@mui/material';
import type { ReactNode } from 'react';

type NavigationPreviewItem = {
  id: string;
  label: string;
  /** The sidebar icon of the destination, so the preview is recognized in the real navigation. */
  icon: ReactNode;
};

type NavigationPreviewGroup = {
  title: string;
  items: NavigationPreviewItem[];
};

type Props = {
  groups: NavigationPreviewGroup[];
  /** Items to point out; the others step back. Empty: every item at rest. */
  highlightedIds?: string[];
  /** Where the real navigation lives, e.g. "Dans le menu, en haut à gauche". */
  caption?: string;
  'data-testid'?: string;
};

type Emphasis = 'rest' | 'highlighted' | 'muted';

const ICON_SIZE = 20;
const ROW_HEIGHT = 4;

const PreviewRoot = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  width: '100%',
}));

const Frame = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5, 1),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
}));

const GroupTitle = styled('p')(({ theme }) => ({
  ...theme.typography.body2Emphasis,
  margin: 0,
  marginBottom: theme.spacing(0.5),
  paddingInline: theme.spacing(1.5),
  color: theme.palette.text.secondary,
}));

const Items = styled('ul')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  margin: 0,
  padding: 0,
  listStyle: 'none',
}));

const Item = styled('li')<{ ownerState: { emphasis: Emphasis } }>(({ theme, ownerState }) => {
  const highlighted = ownerState.emphasis === 'highlighted';
  const muted = ownerState.emphasis === 'muted';

  return {
    ...theme.typography.body2,
    display: 'grid',
    gridTemplateColumns: `${ICON_SIZE}px minmax(0, 1fr)`,
    alignItems: 'center',
    columnGap: theme.spacing(1.5),
    minHeight: theme.spacing(ROW_HEIGHT),
    paddingInline: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    fontWeight: highlighted ? theme.typography.body2Emphasis.fontWeight : theme.typography.body2.fontWeight,
    color: highlighted ? theme.palette.primary.main : theme.palette.text.primary,
    backgroundColor: highlighted
      ? alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity)
      : alpha(theme.palette.primary.main, 0),
    opacity: muted ? theme.palette.action.disabledOpacity : 1,
    transition: theme.transitions.create(['background-color', 'color', 'opacity'], {
      duration: theme.transitions.duration.standard,
      easing: theme.transitions.easing.easeOut,
    }),
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  };
});

const Icon = styled('span')<{ ownerState: { emphasis: Emphasis } }>(({ theme, ownerState }) => ({
  display: 'inline-flex',
  fontSize: ICON_SIZE,
  color: ownerState.emphasis === 'highlighted' ? theme.palette.primary.main : theme.palette.text.secondary,
  transition: theme.transitions.create('color', { duration: theme.transitions.duration.standard }),
  '& > svg': {
    fontSize: 'inherit',
  },
}));

const Label = styled('span')({
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const Caption = styled('p')(({ theme }) => ({
  ...theme.typography.body2,
  margin: 0,
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

function getEmphasis(id: string, highlightedIds: string[]): Emphasis {
  if (highlightedIds.length === 0) return 'rest';
  return highlightedIds.includes(id) ? 'highlighted' : 'muted';
}

/**
 * Inert miniature of the app navigation, to show where destinations live (onboarding, help).
 * Decorative: the surrounding text must name the destinations it points out.
 */
export function NavigationPreview(props: Props) {
  const highlightedIds = props.highlightedIds ?? [];

  return (
    <PreviewRoot data-testid={props['data-testid'] ?? 'navigation-preview'}>
      <Frame aria-hidden="true">
        {props.groups.map((group) => (
          <div key={group.title}>
            <GroupTitle>{group.title}</GroupTitle>
            <Items>
              {group.items.map((item) => {
                const ownerState = { emphasis: getEmphasis(item.id, highlightedIds) };

                return (
                  <Item key={item.id} ownerState={ownerState} data-emphasis={ownerState.emphasis}>
                    <Icon ownerState={ownerState}>{item.icon}</Icon>
                    <Label>{item.label}</Label>
                  </Item>
                );
              })}
            </Items>
          </div>
        ))}
      </Frame>
      {props.caption ? <Caption>{props.caption}</Caption> : null}
    </PreviewRoot>
  );
}

export type {
  NavigationPreviewGroup,
  NavigationPreviewItem,
  Props as NavigationPreviewProps,
};
